import React, { useEffect, useRef, useState } from "react";
import { LOGO_URL } from "../data/mock";
import { Cpu, Network, Brain, Globe, Zap, Sparkles } from "lucide-react";

/**
 * Hero3DLogo
 * Scenografico visual hero con:
 * - Logo 3D che ruota su se stesso
 * - Trasformazione ciclica: Logo → Tech imagery → Tagline → Logo
 * - Particelle, connessioni neurali, glow dinamico
 * - Parallax sul movimento del mouse
 */
export default function Hero3DLogo() {
  const [phase, setPhase] = useState(0); // 0 logo, 1 tech, 2 text, 3 back to logo
  const wrapRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  // Animation phase cycler
  useEffect(() => {
    const seq = [0, 1, 2, 0]; // logo → tech → text → logo
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % seq.length;
      setPhase(seq[i]);
    }, 3200);
    return () => clearInterval(id);
  }, []);

  // Mouse parallax
  useEffect(() => {
    const onMove = (e) => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / r.width;
      const dy = (e.clientY - cy) / r.height;
      setTilt({ x: Math.max(-1, Math.min(1, dx)), y: Math.max(-1, Math.min(1, dy)) });
    };
    const onLeave = () => setTilt({ x: 0, y: 0 });
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const rx = -tilt.y * 10;
  const ry = tilt.x * 14;

  return (
    <div
      ref={wrapRef}
      className="relative aspect-square max-w-[560px] mx-auto select-none"
      style={{ perspective: "1400px" }}
    >
      {/* Outer aura */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#102a6b]/15 via-transparent to-[#f59e0b]/15 blur-3xl animate-pulse-slow" />

      {/* Orbital rings */}
      <Orbit rotateZ={0} scale={1.0} color="rgba(16,42,107,0.18)" />
      <Orbit rotateZ={45} scale={0.78} color="rgba(245,158,11,0.28)" />
      <Orbit rotateZ={-30} scale={0.58} color="rgba(16,42,107,0.12)" />

      {/* Floating particles */}
      <Particles />

      {/* Center stage 3D card */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ transformStyle: "preserve-3d" }}
      >
        <div
          className="relative transition-transform duration-700 ease-[cubic-bezier(.2,.7,.2,1)]"
          style={{
            transformStyle: "preserve-3d",
            transform: `rotateX(${rx}deg) rotateY(${ry}deg)`,
            width: 340,
            height: 340,
          }}
        >
          {/* LOGO FACE */}
          <Face active={phase === 0 || phase === 3}>
            <div className="relative">
              {/* Glow halo */}
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-[#f59e0b]/30 via-transparent to-[#102a6b]/30 blur-2xl" />
              <div className="relative bg-white rounded-[2rem] border border-slate-200 shadow-[0_30px_80px_-20px_rgba(16,42,107,0.45)] p-7 md:p-9 logo-float">
                {/* Inner reflective panel */}
                <div className="absolute inset-2 rounded-[1.6rem] bg-gradient-to-br from-white via-white to-slate-50 pointer-events-none" />
                <img
                  src={LOGO_URL}
                  alt="A.C.D.T."
                  className="relative h-44 w-44 md:h-56 md:w-56 object-contain drop-shadow-xl"
                  style={{ filter: "drop-shadow(0 12px 24px rgba(16,42,107,0.25))" }}
                />
                {/* Shine overlay */}
                <div className="absolute inset-0 rounded-[2rem] pointer-events-none overflow-hidden">
                  <div className="absolute -top-1/2 left-0 right-0 h-full bg-gradient-to-b from-white/40 to-transparent rotate-12 translate-x-[-30%] shine-sweep" />
                </div>
                {/* Glint dots around logo */}
                <span className="absolute top-2 right-3 h-1.5 w-1.5 rounded-full bg-[#f59e0b] glint" />
                <span className="absolute bottom-4 left-3 h-1 w-1 rounded-full bg-[#102a6b] glint" style={{ animationDelay: "1.2s" }} />
                <span className="absolute top-1/2 right-2 h-1 w-1 rounded-full bg-[#fbbf24] glint" style={{ animationDelay: "0.6s" }} />
              </div>
            </div>
          </Face>

          {/* TECH FACE */}
          <Face active={phase === 1}>
            <div className="relative h-[280px] w-[280px] md:h-[340px] md:w-[340px] rounded-[2rem] bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] border border-white/10 shadow-[0_30px_80px_-20px_rgba(16,42,107,0.6)] overflow-hidden flex items-center justify-center">
              {/* Circuit grid */}
              <CircuitGrid />

              {/* Synapse network */}
              <SynapseNetwork />

              {/* Random flash bursts */}
              <span className="absolute top-[18%] left-[22%] h-2 w-2 rounded-full bg-[#fbbf24] flash-burst" />
              <span className="absolute top-[30%] right-[20%] h-1.5 w-1.5 rounded-full bg-white flash-burst" style={{ animationDelay: "0.7s" }} />
              <span className="absolute bottom-[28%] left-[28%] h-2 w-2 rounded-full bg-[#f59e0b] flash-burst" style={{ animationDelay: "1.3s" }} />
              <span className="absolute bottom-[18%] right-[26%] h-1.5 w-1.5 rounded-full bg-white flash-burst" style={{ animationDelay: "1.9s" }} />
              <span className="absolute top-1/2 left-1/2 h-1 w-1 rounded-full bg-[#fbbf24] flash-burst" style={{ animationDelay: "0.3s" }} />

              {/* Lightning bolts */}
              <LightningSVG />

              {/* Glowing nodes */}
              <div className="relative grid grid-cols-3 gap-6 z-10">
                <TechNode icon={Brain} label="AI" delay="0s" />
                <TechNode icon={Network} label="Network" delay=".15s" />
                <TechNode icon={Cpu} label="Cpu" delay=".3s" />
                <TechNode icon={Globe} label="Globe" delay=".45s" />
                <TechNode icon={Sparkles} label="Spark" delay=".6s" big />
                <TechNode icon={Zap} label="Zap" delay=".75s" />
              </div>

              {/* Scan lines (horizontal and vertical) */}
              <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24] to-transparent scanline" />
              <div className="absolute inset-y-0 left-0 w-[2px] bg-gradient-to-b from-transparent via-[#f59e0b] to-transparent scanline-v" />

              {/* Corner glow */}
              <div className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-[#fbbf24]/40 blur-2xl corner-pulse" />
              <div className="absolute -bottom-10 -left-10 h-28 w-28 rounded-full bg-[#1e40af]/40 blur-2xl corner-pulse" style={{ animationDelay: "1s" }} />
            </div>
          </Face>

          {/* TEXT FACE */}
          <Face active={phase === 2}>
            <div className="relative h-[280px] w-[280px] md:h-[340px] md:w-[340px] rounded-[2rem] bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] border border-white/10 shadow-[0_30px_80px_-20px_rgba(16,42,107,0.6)] flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 grid-bg opacity-30" />
              <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-[#f59e0b]/30 blur-3xl" />
              <div className="relative text-center px-6">
                <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[#fbbf24]">
                  A.C.D.T.
                </div>
                <div className="mt-3 text-[26px] md:text-[32px] font-extrabold text-white leading-[1.05]">
                  Le nuove
                  <br />
                  <span className="bg-gradient-to-r from-[#fbbf24] via-[#f59e0b] to-[#fbbf24] bg-clip-text text-transparent">
                    connessioni
                  </span>
                  <br />
                  digitali
                </div>
                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-semibold text-white">
                  <Sparkles className="h-3 w-3 text-[#fbbf24]" />
                  Innovazione · Formazione · Sostenibilità
                </div>
              </div>
            </div>
          </Face>
        </div>
      </div>

      {/* Floating chips */}
      <FloatChip className="-left-2 top-10" text="✓ Legge 4/2013" delay="-2s" />
      <FloatChip className="right-0 top-32" text="✓ MIMIT" delay="-4s" />
      <FloatChip className="left-6 bottom-6" text="Badge Digitale" delay="-1s" />
      <FloatChip className="-right-2 bottom-16" text="CFDT · Crediti" amber delay="-3s" />

      <style>{css}</style>
    </div>
  );
}

function Face({ active, children }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center transition-all duration-700 ease-[cubic-bezier(.2,.7,.2,1)]"
      style={{
        opacity: active ? 1 : 0,
        transform: `rotateY(${active ? 0 : 90}deg) scale(${active ? 1 : 0.9})`,
        pointerEvents: active ? "auto" : "none",
      }}
    >
      {children}
    </div>
  );
}

function Orbit({ rotateZ, scale, color }) {
  return (
    <div
      className="absolute inset-0 rounded-full border"
      style={{
        borderColor: color,
        transform: `rotate(${rotateZ}deg) scale(${scale})`,
      }}
    />
  );
}

function Particles() {
  const dots = [
    { d: 0, deg: 0 },
    { d: 0.4, deg: 60 },
    { d: 0.8, deg: 120 },
    { d: 1.2, deg: 180 },
    { d: 1.6, deg: 240 },
    { d: 2.0, deg: 300 },
    { d: 0.2, deg: 30 },
    { d: 0.6, deg: 90 },
    { d: 1.0, deg: 150 },
  ];
  return (
    <>
      {dots.map((p, i) => (
        <span
          key={i}
          className={`absolute top-1/2 left-1/2 rounded-full ${
            i % 2 === 0 ? "bg-[#102a6b]" : "bg-[#f59e0b]"
          } orbit-spin`}
          style={{
            height: i % 3 === 0 ? 10 : 6,
            width: i % 3 === 0 ? 10 : 6,
            transform: `translate(-50%,-50%) rotate(${p.deg}deg) translateY(-${
              180 + (i % 3) * 15
            }px)`,
            animationDelay: `${p.d}s`,
            boxShadow: `0 0 14px ${i % 2 === 0 ? "rgba(16,42,107,0.6)" : "rgba(245,158,11,0.7)"}`,
          }}
        />
      ))}
    </>
  );
}

function NeuralSVG({ active }) {
  return (
    <svg
      className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-700 ${
        active ? "opacity-70" : "opacity-0"
      }`}
      viewBox="0 0 400 400"
      fill="none"
    >
      <defs>
        <linearGradient id="ln" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#102a6b" stopOpacity="0.0" />
          <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#102a6b" stopOpacity="0.0" />
        </linearGradient>
      </defs>
      {[
        ["120,80", "200,200"],
        ["280,90", "200,200"],
        ["80,220", "200,200"],
        ["320,220", "200,200"],
        ["140,320", "200,200"],
        ["260,330", "200,200"],
      ].map(([a, b], i) => {
        const [x1, y1] = a.split(",").map(Number);
        const [x2, y2] = b.split(",").map(Number);
        return (
          <g key={i}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="url(#ln)"
              strokeWidth="1.5"
              strokeDasharray="6 6"
              className="neural-line"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
            <circle cx={x1} cy={y1} r={3.5} fill="#f59e0b" />
          </g>
        );
      })}
    </svg>
  );
}

function CircuitGrid() {
  return (
    <div className="absolute inset-0 opacity-25">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(245,158,11,.45) 1px,transparent 1px), linear-gradient(90deg,rgba(245,158,11,.45) 1px,transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />
    </div>
  );
}

function SynapseNetwork() {
  // 6 nodes positioned around the grid; we'll draw flickering connections
  const nodes = [
    { x: 60, y: 80 },
    { x: 200, y: 50 },
    { x: 290, y: 110 },
    { x: 80, y: 220 },
    { x: 230, y: 250 },
    { x: 170, y: 170 },
  ];
  const links = [
    [0, 1], [1, 2], [0, 3], [3, 5], [5, 4], [2, 4], [1, 5], [3, 4],
  ];
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 340 340" fill="none">
      <defs>
        <linearGradient id="synapse" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fbbf24" stopOpacity="0" />
          <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
        </linearGradient>
        <radialGradient id="nodeGlow">
          <stop offset="0%" stopColor="#fbbf24" stopOpacity="1" />
          <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
        </radialGradient>
      </defs>
      {links.map((l, i) => {
        const a = nodes[l[0]];
        const b = nodes[l[1]];
        return (
          <line
            key={i}
            x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            stroke="url(#synapse)"
            strokeWidth="1.5"
            strokeDasharray="4 6"
            className="synapse-line"
            style={{ animationDelay: `${i * 0.18}s` }}
          />
        );
      })}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r="10" fill="url(#nodeGlow)" className="synapse-glow" style={{ animationDelay: `${i * 0.2}s` }} />
          <circle cx={n.x} cy={n.y} r="2.5" fill="#fbbf24" />
        </g>
      ))}
    </svg>
  );
}

function LightningSVG() {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 340 340" fill="none">
      <path
        d="M40 60 L90 130 L70 145 L130 240"
        stroke="#fbbf24"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lightning"
        style={{ filter: "drop-shadow(0 0 6px #fbbf24)" }}
      />
      <path
        d="M280 80 L240 150 L260 165 L210 250"
        stroke="#f59e0b"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="lightning"
        style={{ animationDelay: "1.4s", filter: "drop-shadow(0 0 6px #f59e0b)" }}
      />
    </svg>
  );
}

function TechNode({ icon: Icon, big, delay }) {
  return (
    <div
      className={`flex items-center justify-center rounded-xl bg-white/10 border border-white/15 backdrop-blur-sm node-pop`}
      style={{
        height: big ? 80 : 64,
        width: big ? 80 : 64,
        animationDelay: delay,
      }}
    >
      <Icon
        className={`text-[#fbbf24] ${big ? "h-8 w-8" : "h-6 w-6"}`}
        strokeWidth={2}
      />
    </div>
  );
}

function FloatChip({ className = "", text, delay = "0s", amber = false }) {
  return (
    <div
      className={`absolute float-y bg-white rounded-xl shadow-lg border border-slate-100 px-3 py-2 text-[12px] font-semibold ${
        amber ? "text-[#b45309]" : "text-[#102a6b]"
      } ${className}`}
      style={{ animationDelay: delay }}
    >
      {text}
    </div>
  );
}

const css = `
@keyframes orbit-spin {
  from { transform: translate(-50%,-50%) rotate(0deg) translateY(-180px); }
  to { transform: translate(-50%,-50%) rotate(360deg) translateY(-180px); }
}
.orbit-spin { animation: orbit-spin 12s linear infinite; }

@keyframes pulse-slow {
  0%,100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 0.85; transform: scale(1.04); }
}
.animate-pulse-slow { animation: pulse-slow 4.5s ease-in-out infinite; }

@keyframes shine-sweep {
  0% { transform: translateX(-30%) translateY(-100%) rotate(12deg); opacity: 0; }
  20% { opacity: 0.55; }
  100% { transform: translateX(140%) translateY(150%) rotate(12deg); opacity: 0; }
}
.shine-sweep { animation: shine-sweep 4s ease-in-out infinite; }

/* Logo gentle 3D float — keeps text always readable left-to-right */
@keyframes logo-float {
  0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
  25% { transform: translateY(-6px) rotate(-1.2deg) scale(1.01); }
  50% { transform: translateY(-10px) rotate(0deg) scale(1.02); }
  75% { transform: translateY(-6px) rotate(1.2deg) scale(1.01); }
}
.logo-float { animation: logo-float 5s ease-in-out infinite; }

/* Small sparkle dots glinting on the logo card */
@keyframes glint {
  0%, 100% { opacity: 0; transform: scale(0.6); }
  50% { opacity: 1; transform: scale(1.4); box-shadow: 0 0 12px currentColor; }
}
.glint { animation: glint 2.2s ease-in-out infinite; }

@keyframes scanline {
  0% { transform: translateY(0); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translateY(340px); opacity: 0; }
}
.scanline { animation: scanline 2.5s ease-in-out infinite; }

@keyframes scanline-v {
  0% { transform: translateX(0); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translateX(340px); opacity: 0; }
}
.scanline-v { animation: scanline-v 3.2s ease-in-out infinite; }

/* Synapse flickering lines */
@keyframes synapse-line {
  0% { stroke-dashoffset: 30; opacity: 0; }
  20% { opacity: 0.3; }
  50% { opacity: 1; }
  80% { opacity: 0.3; }
  100% { stroke-dashoffset: 0; opacity: 0; }
}
.synapse-line {
  animation: synapse-line 2.2s ease-in-out infinite;
}

/* Glow around synapse nodes */
@keyframes synapse-glow {
  0%, 100% { opacity: 0.3; transform-origin: center; }
  50% { opacity: 1; }
}
.synapse-glow {
  animation: synapse-glow 1.8s ease-in-out infinite;
  transform-box: fill-box;
}

/* Lightning bolt flash */
@keyframes lightning {
  0%, 88%, 100% { opacity: 0; }
  90% { opacity: 1; }
  92% { opacity: 0; }
  94% { opacity: 0.9; }
  96% { opacity: 0; }
}
.lightning { animation: lightning 3.5s ease-in-out infinite; }

/* Bursting flash dots */
@keyframes flash-burst {
  0%, 100% { opacity: 0; transform: scale(0.4); }
  50% { opacity: 1; transform: scale(2.2); box-shadow: 0 0 18px currentColor; }
}
.flash-burst { animation: flash-burst 1.6s ease-in-out infinite; }

/* Corner halo pulse */
@keyframes corner-pulse {
  0%, 100% { opacity: 0.35; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.25); }
}
.corner-pulse { animation: corner-pulse 2.4s ease-in-out infinite; }

@keyframes node-pop {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 rgba(251,191,36,0); }
  50% { transform: scale(1.08); box-shadow: 0 0 28px rgba(251,191,36,0.45); }
}
.node-pop { animation: node-pop 2.2s ease-in-out infinite; }
`;
